if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        if (!this.elements.viewer) return;
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.colorFilterConnected) return;
        this.colorFilterConnected = true;

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.colorVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
            const selectedColor = this.getSelectedColor(data?.html);
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                if (this.isConnected) this.filterMediaByColor(selectedColor);
              });
            });
          });
        }

        window.requestAnimationFrame(() => this.filterMediaByColor());
      }

      disconnectedCallback() {
        this.colorVariantUnsubscriber?.();
        this.colorVariantUnsubscriber = undefined;
        this.colorFilterConnected = false;
      }

      normalizeColorText(value) {
        return String(value)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      getSelectedColor(root) {
        const scope = root || this.closest('product-info') || document;
        const checkedOptions = Array.from(scope.querySelectorAll('input[type="radio"]:checked'));
        const colorOption = checkedOptions.find((input) => {
          const optionName = this.normalizeColorText(input.name);
          return optionName === 'couleur' || optionName === 'color' || optionName.startsWith('couleur ') || optionName.startsWith('color ');
        });
        const colorSelect =
          scope.querySelector('select[name*="Couleur"]') || scope.querySelector('select[name*="Color"]');
        return this.normalizeColorText(colorOption?.value || colorSelect?.value || '');
      }

      filterMediaByColor(selectedColorOverride = '') {
        const productInfo = this.closest('product-info') || document;
        const checkedOptions = Array.from(productInfo.querySelectorAll('input[type="radio"]:checked'));
        const colorOption = checkedOptions.find((input) => {
          const optionName = this.normalizeColorText(input.name);
          return optionName === 'couleur' || optionName === 'color' || optionName.startsWith('couleur ') || optionName.startsWith('color ');
        });
        const colorSelect =
          productInfo.querySelector('select[name*="Couleur"]') || productInfo.querySelector('select[name*="Color"]');
        const selectedColor = this.normalizeColorText(selectedColorOverride || colorOption?.value || colorSelect?.value || '');

        const optionName = colorOption?.name;
        const colorInputs = optionName
          ? Array.from(productInfo.querySelectorAll('input[type="radio"]')).filter((input) => input.name === optionName)
          : [];
        const colorValues = colorInputs.map((input) => this.normalizeColorText(input.value)).filter(Boolean);
        if (colorSelect) {
          Array.from(colorSelect.options).forEach((option) => {
            const value = this.normalizeColorText(option.value);
            if (value && !colorValues.includes(value)) colorValues.push(value);
          });
        }

        const orderedColorValues = [...colorValues].sort((a, b) => b.length - a.length);

        const itemMatchesColor = (item) => {
          const image = item.querySelector('img');
          const alt = this.normalizeColorText(image?.alt || '');
          const source = ` ${this.normalizeColorText(image?.currentSrc || image?.src || '')} `;

          const isSizeGuide =
            (alt.includes('guide') && alt.includes('taille')) ||
            (source.includes(' guide ') && source.includes(' taille'));
          if (isSizeGuide) return false;
          if (!selectedColor || !orderedColorValues.length) return true;

          const comparableAlt = ` ${alt.replace(/\bborreguito\b/g, ' ').replace(/\s+/g, ' ').trim()} `;
          const comparableSource = ` ${source.replace(/\bborreguito\b/g, ' ').replace(/\s+/g, ' ').trim()} `;

          const altColor = orderedColorValues.find(
            (color) =>
              comparableAlt.includes(`couleur ${color}`) ||
              comparableAlt.includes(`color ${color}`)
          );
          const sourceColor = orderedColorValues.find(
            (color) => comparableSource.includes(` ${color} `)
          );
          const mediaColor = altColor || sourceColor;
          return !mediaColor || mediaColor === selectedColor;
        };

        const viewerItems = Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id]') || []);
        viewerItems.forEach((item) => {
          const visible = itemMatchesColor(item);
          item.hidden = !visible;
          item.style.display = visible ? '' : 'none';
        });

        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('li[data-target]') || []);
        thumbnailItems.forEach((item) => {
          const target = item.dataset.target;
          const viewerItem = viewerItems.find((media) => media.dataset.mediaId === target);
          const visible = viewerItem ? !viewerItem.hidden : itemMatchesColor(item);
          item.hidden = !visible;
          if (!visible) item.style.display = 'none';
          else item.style.removeProperty('display');
        });

        const visibleThumbnailTargets = new Set(
          thumbnailItems
            .filter((item) => !item.hidden && window.getComputedStyle(item).display !== 'none')
            .map((item) => item.dataset.target)
        );

        if (visibleThumbnailTargets.size) {
          viewerItems.forEach((item) => {
            const visible = visibleThumbnailTargets.has(item.dataset.mediaId);
            item.hidden = !visible;
            item.style.display = visible ? '' : 'none';
          });
        }

        const activeItem = viewerItems.find((item) => item.classList.contains('is-active'));
        if (!activeItem || activeItem.hidden) {
          const firstVisible = viewerItems.find((item) => !item.hidden);
          if (firstVisible) this.setActiveMedia(firstVisible.dataset.mediaId, false, true);
        }

        this.elements.viewer?.slider?.resetPages?.();
        this.elements.thumbnails?.slider?.resetPages?.();
      }

      onSlideChanged(event) {
        if (!event.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, preventPageScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (preventPageScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const thumbnailButton = thumbnail.querySelector('button');
        if (!thumbnailButton) return;
        thumbnailButton.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
